import { IBulkPriceChange } from '@/interfaces/ICatalogV2';
import { IItem } from '@/interfaces/IItem';
import { MAX_PRICE, parseMoneyInput } from '@/domain/store-setup/money';
import { hasSizes, sortedSizes } from './catalog-filters';

/**
 * «Cambiar precio de {n} productos» (plan §5.10): raise or lower by a
 * percentage or an amount, optionally rounded to a currency step (up when
 * raising, down when lowering). It changes the price, the discounted price
 * and every alive size's cost and discounted cost.
 */
export type TBulkPriceDirection = 'up' | 'down';
export type TBulkPriceUnit = 'percent' | 'amount';

export interface IBulkPriceInput {
  direction: TBulkPriceDirection;
  unit: TBulkPriceUnit;
  value: number;
  /** Rounding step; null = «No redondear». */
  rounding: number | null;
}

const STEPS_BY_CURRENCY: Record<string, number[]> = {
  EUR: [0.1, 0.5, 1],
  USD: [0.1, 0.5, 1],
  ARS: [10, 50, 100, 500],
  UYU: [10, 50, 100, 500],
  CLP: [100, 500, 1000],
  COP: [100, 500, 1000],
};

const DEFAULT_STEPS = [0.1, 0.5, 1];

/** Steps offered for the currency; never one ≥ the lowest selected price. */
export function roundingSteps(currency: string, lowestPrice: number): number[] {
  const steps = STEPS_BY_CURRENCY[currency] ?? DEFAULT_STEPS;
  return steps.filter((step) => step < lowestPrice);
}

function toCents(value: number): number {
  return Math.round(value * 100);
}

/** One amount after the change (2 decimals). */
export function applyBulkAmount(
  amount: number,
  input: IBulkPriceInput
): number {
  const sign = input.direction === 'up' ? 1 : -1;
  const changedCents =
    input.unit === 'percent'
      ? Math.round(toCents(amount) * (1 + (sign * input.value) / 100))
      : toCents(amount) + sign * toCents(input.value);

  if (!input.rounding) return changedCents / 100;
  const step = toCents(input.rounding);
  const rounded =
    input.direction === 'up'
      ? Math.ceil(changedCents / step) * step
      : Math.floor(changedCents / step) * step;
  return rounded / 100;
}

export type TBulkPriceProblem = 'nonPositive' | 'tooHigh' | 'discount';

export interface IBulkPricePreviewLine {
  /** The size title; null for the single price. */
  label: string | null;
  before: number;
  after: number;
  discountBefore: number | null;
  discountAfter: number | null;
  /** Rounding moved it the other way (a raise that lowers it). */
  wrongSide: boolean;
  problem: TBulkPriceProblem | null;
}

export interface IBulkPricePreviewItem {
  itemId: number;
  name: string;
  lines: IBulkPricePreviewLine[];
}

export interface IBulkPriceResult {
  preview: IBulkPricePreviewItem[];
  changes: IBulkPriceChange[];
  /** Some changed line would be invalid: «Aplicar» stays disabled. */
  blocked: boolean;
}

function previewLine(
  label: string | null,
  before: number,
  discountBefore: number | null,
  input: IBulkPriceInput
): IBulkPricePreviewLine {
  const after = applyBulkAmount(before, input);
  const discountAfter =
    discountBefore !== null ? applyBulkAmount(discountBefore, input) : null;
  const changed = after !== before || discountAfter !== discountBefore;
  const wrongSide = input.direction === 'up' ? after < before : after > before;

  let problem: TBulkPriceProblem | null = null;
  if (changed) {
    if (after <= 0 || (discountAfter !== null && discountAfter <= 0))
      problem = 'nonPositive';
    else if (after > MAX_PRICE) problem = 'tooHigh';
    else if (discountAfter !== null && discountAfter >= after)
      problem = 'discount';
  }
  return {
    label,
    before,
    after,
    discountBefore,
    discountAfter,
    wrongSide,
    problem,
  };
}

function hasChange(line: IBulkPricePreviewLine): boolean {
  return (
    line.after !== line.before || line.discountAfter !== line.discountBefore
  );
}

export function applyBulkPrice(
  items: readonly IItem[],
  input: IBulkPriceInput
): IBulkPriceResult {
  const preview: IBulkPricePreviewItem[] = [];
  const changes: IBulkPriceChange[] = [];

  for (const item of items) {
    if (hasSizes(item)) {
      const sizes = sortedSizes(item).filter((size) => size.id !== undefined);
      const lines = sizes.map((size) =>
        previewLine(
          size.title,
          Number(size.cost),
          size.discountedCost != null ? Number(size.discountedCost) : null,
          input
        )
      );
      preview.push({ itemId: item.id, name: item.name, lines });
      const changedSizes = sizes.flatMap((size, index) =>
        size.id !== undefined && hasChange(lines[index])
          ? [
              {
                id: size.id,
                cost: lines[index].after,
                ...(lines[index].discountAfter !== null
                  ? { discountedCost: lines[index].discountAfter }
                  : {}),
              },
            ]
          : []
      );
      if (changedSizes.length)
        changes.push({ itemId: item.id, sizes: changedSizes });
      continue;
    }

    const line = previewLine(
      null,
      Number(item.price),
      item.discountedPrice != null ? Number(item.discountedPrice) : null,
      input
    );
    preview.push({ itemId: item.id, name: item.name, lines: [line] });
    if (hasChange(line))
      changes.push({
        itemId: item.id,
        price: line.after,
        ...(line.discountAfter !== null
          ? { discountedPrice: line.discountAfter }
          : {}),
      });
  }

  return {
    preview,
    changes,
    blocked: preview.some((row) => row.lines.some((line) => !!line.problem)),
  };
}

/** The lowest price among the selection (sizes included), for the rounding steps. */
export function lowestSelectedPrice(items: readonly IItem[]): number {
  const prices = items.flatMap((item) =>
    hasSizes(item)
      ? sortedSizes(item).map((size) => Number(size.cost))
      : [Number(item.price)]
  );
  const positive = prices.filter((price) => price > 0);
  return positive.length ? Math.min(...positive) : 0;
}

/** A raise above this percentage is surely a typo. */
export const MAX_BULK_PERCENT = 1000;

/** «10», «2,5», «1.500»: the change to apply; null when it is not a valid one. */
export function parseBulkValue(
  text: string,
  unit: TBulkPriceUnit
): number | null {
  const value = parseMoneyInput(text, { allowZero: false });
  if (value === null) return null;
  return unit === 'percent' && value > MAX_BULK_PERCENT ? null : value;
}
